import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ArrowLeft, FileSpreadsheet, Users, X, Send, Copy, Palette, FolderLock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import PromptDialog from '@/components/ui/PromptDialog';
import {
  qk, fetchClient, fetchStrategiesByClient, fetchClientTeam, addTeamMember, removeTeamMember,
  fetchClientContacts, inviteClientContact,
  fetchAdminUsers, setClientSettings, type Client,
  talentTelegramCode, unlinkTalentTelegram, invitePortalDoctor, type TalentCode,
} from '@/lib/queries';
import ApproverRoutingCard from '@/components/creative/ApproverRouting';
import MeetingRoutingCard from '@/components/client/MeetingRoutingCard';
import ClientLocationsCard from '@/components/creative/ClientLocations';
import WhatsAppLinkDialog from '@/components/connect/WhatsAppLinkDialog';
import { Pill } from '@/components/connect/MyChannelsCard';
import { saveTalentContact, talentWhatsAppLink, talentEmailConfirm, talentWhatsAppUnlink, userWhatsAppLink, userEmailConfirm, errText } from '@/lib/connections';
import { useCan } from '@/hooks/use-scopes';
import { humanize, periodLabel, seenLabel } from '@/lib/format';
import { cn } from '@/lib/cn';

const STATUS_COLOR: Record<string, string> = {
  draft:    'bg-slate-700/40 text-slate-300',
  active:   'bg-emerald-500/15 text-emerald-300',
  done:     'bg-accent-600/20 text-accent-400',
  archived: 'bg-slate-700/40 text-slate-400',
};

export default function ClientDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useQuery({
    queryKey: qk.client(id),
    queryFn: () => fetchClient(id),
    enabled: !!id,
  });
  const strategies = useQuery({
    queryKey: qk.strategiesByClient(id),
    queryFn: () => fetchStrategiesByClient(id),
    enabled: !!id,
  });

  const canEditClient = useCan('clients.write');
  const canPlanStrategy = useCan('strategies.write');
  // the documents vault is sensitive (IDs, licences): shown only to people granted it
  const canDocsRead = useCan('documents.read');
  const canDocsWrite = useCan('documents.write');

  if (!id) return <Navigate to="/clients" replace />;
  if (client.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!client.data) return <Navigate to="/clients" replace />;

  const c = client.data;

  return (
    <div>
      <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to clients
      </Link>
      <PageHeader
        title={c.name}
        subtitle={`${c.vertical.replace('_', ' ')} - ${c.taxonomy.primary.values.length} ${c.taxonomy.primary.label.toLowerCase()} values`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link to={`/clients/${c.id}/brand-kit`}>
              <Button variant="secondary" icon={<Palette size={14} />} data-testid="open-brand-kit">Brand kit</Button>
            </Link>
            {(canDocsRead || canDocsWrite) && (
              <Link to={`/clients/${c.id}/documents`}>
                <Button variant="secondary" icon={<FolderLock size={14} />} data-testid="open-documents">Documents</Button>
              </Link>
            )}
            {canEditClient && (
              <Link to={`/clients/${c.id}/taxonomy`}>
                <Button variant="secondary" icon={<FileSpreadsheet size={14} />}>Edit taxonomy</Button>
              </Link>
            )}
            {canPlanStrategy && (
              <Link to={`/strategies/new?clientId=${c.id}`}>
                <Button icon={<Plus size={14} />}>New strategy</Button>
              </Link>
            )}
          </div>
        }
      />

      <Card className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium text-muted-strong">Taxonomy</h2>
          <Link to={`/clients/${c.id}/taxonomy`} className="text-xs text-accent-400 hover:underline">
            Edit
          </Link>
        </div>
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                <th className="font-medium py-2 pr-3 whitespace-nowrap w-24">Axis</th>
                <th className="font-medium py-2 pr-3 whitespace-nowrap">Label</th>
                <th className="font-medium py-2 pr-3 whitespace-nowrap text-right w-16">Count</th>
                <th className="font-medium py-2">Values</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-line">
                <td className="py-3 pr-3 text-[11px] uppercase tracking-wider text-muted whitespace-nowrap">Primary</td>
                <td className="py-3 pr-3 text-muted-strong whitespace-nowrap">{c.taxonomy.primary.label}</td>
                <td className="py-3 pr-3 text-right font-mono text-xs text-muted">{c.taxonomy.primary.values.length}</td>
                <td className="py-3 text-sm text-muted-strong">
                  {c.taxonomy.primary.values.length
                    ? c.taxonomy.primary.values.join(', ')
                    : <span className="text-muted italic">none</span>}
                </td>
              </tr>
              <tr className="border-b border-line">
                <td className="py-3 pr-3 text-[11px] uppercase tracking-wider text-muted whitespace-nowrap">Secondary</td>
                <td className="py-3 pr-3 text-muted-strong whitespace-nowrap">{c.taxonomy.secondary.label}</td>
                <td className="py-3 pr-3 text-right font-mono text-xs text-muted">{c.taxonomy.secondary.values.length}</td>
                <td className="py-3 text-sm text-muted-strong">
                  {c.taxonomy.secondary.values.length
                    ? c.taxonomy.secondary.values.join(', ')
                    : <span className="text-muted italic">none</span>}
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-3 text-[11px] uppercase tracking-wider text-muted whitespace-nowrap">Talent</td>
                <td className="py-3 pr-3 text-muted-strong whitespace-nowrap">
                  {c.taxonomy.talent.enabled
                    ? c.taxonomy.talent.label
                    : <span className="text-muted italic">disabled</span>}
                </td>
                <td className="py-3 pr-3 text-right font-mono text-xs text-muted">
                  {c.taxonomy.talent.enabled ? c.talents.length : '-'}
                </td>
                <td className="py-3 text-sm text-muted-strong">
                  {c.taxonomy.talent.enabled
                    ? (c.talents.length
                        ? c.talents.slice(0, 6).map(t => t.name).join(', ') + (c.talents.length > 6 ? ` (+${c.talents.length - 6} more)` : '')
                        : <span className="text-muted italic">no talents yet</span>)
                    : <span className="text-muted italic">tracking off</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <ApproverRoutingCard clientId={c.id} />
      <MeetingRoutingCard clientId={c.id} />
      <TalentsCard client={c} />
      <TeamCard clientId={c.id} />
      <ClientLocationsCard clientId={c.id} />
      <ContactsCard clientId={c.id} />
      <TermsCard client={c} />

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium text-muted-strong">Strategies</h2>
          <span className="text-xs text-muted">
            {strategies.isLoading ? '...' : `${strategies.data?.length ?? 0} total`}
          </span>
        </div>
        {(strategies.data?.length ?? 0) === 0 ? (
          <div className="text-sm text-muted py-6 text-center">
            No strategies yet for this client.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Code</th>
                  <th className="font-medium py-2 pr-3">Title</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Period</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Funnel mix</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden lg:table-cell">Slots</th>
                  <th className="font-medium py-2 whitespace-nowrap text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {strategies.data!.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/strategies/${s.id}`)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-bg-soft transition"
                  >
                    <td className="py-3 pr-3 font-mono text-xs text-accent-400 whitespace-nowrap">{s.code}</td>
                    <td className="py-3 pr-3 text-muted-strong">{s.title}</td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">
                      {periodLabel(s.period_start, s.period_end)}
                    </td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">
                      <span className="font-mono">{s.funnel_mix.TOF}/{s.funnel_mix.MOF}/{s.funnel_mix.BOF}</span>
                      <span className="text-muted/60 ml-1">TOF/MOF/BOF</span>
                    </td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden lg:table-cell">
                      {s.slots.length}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <span className={cn(
                        'inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full',
                        STATUS_COLOR[s.status] ?? STATUS_COLOR.draft,
                      )}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


/** Who is on this client. The same data as the Clinics dialog in Admin, seen
 *  from the clinic's side, so a new clinic is never left with nobody on it. */
function TeamCard({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const canEdit = useCan('users.scope');
  const team = useQuery({ queryKey: qk.clientTeam(clientId), queryFn: () => fetchClientTeam(clientId) });
  const staff = useQuery({ queryKey: ['admin-users', 'team-pick'], queryFn: () => fetchAdminUsers({ active: true }), enabled: canEdit });
  const [pick, setPick] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: qk.clientTeam(clientId) });
  const add = useMutation({
    mutationFn: (userId: string) => addTeamMember(clientId, userId),
    onSuccess: () => { toast.success('Added to the team'); setPick(''); refresh(); qc.invalidateQueries({ queryKey: ['admin-users'] }); },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeTeamMember(clientId, userId),
    onSuccess: () => { toast.success('Removed from the team'); refresh(); qc.invalidateQueries({ queryKey: ['admin-users'] }); },
  });
  const onTeam = new Set((team.data ?? []).map((m) => m.id));
  const options = (staff.data ?? [])
    .filter((u) => u.role !== 'client' && !u.unrestricted && !onTeam.has(u.id))
    .map((u) => ({ value: u.id, label: u.display_name, hint: humanize(u.designations[0] ?? u.role) }));

  return (
    <div data-testid="team-card">
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-accent-400" />
          <h2 className="font-medium text-muted-strong">Team</h2>
          <span className="text-xs text-muted">{team.data ? `${team.data.length} on this client` : ''}</span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Select value={pick} onChange={setPick} searchable size="sm" placeholder="Add a person..." className="w-56"
              options={options.length ? options : [{ value: '', label: 'Everyone is on this client', disabled: true }]} />
            <Button size="sm" icon={<Plus size={12} />} disabled={!pick || add.isPending} onClick={() => pick && add.mutate(pick)}>Add</Button>
          </div>
        )}
      </div>
      {team.isLoading && <div className="text-xs text-muted">Loading...</div>}
      {team.data && team.data.length === 0 && (
        <div className="text-sm text-amber-400 py-3">Nobody is on this client yet. Only the owner and managers can see it.</div>
      )}
      {team.data && team.data.length > 0 && (
        <ul className="divide-y divide-line">
          {team.data.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-2">
              <span className="text-sm text-muted-strong">{m.display_name}</span>
              <span className="text-[11px] text-muted">{humanize(m.designations[0] ?? m.role)}</span>
              <span className={
                'ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded border ' +
                (m.access_level === 'clients' ? 'text-sky-300 border-sky-500/30 bg-sky-500/10' : 'text-amber-300 border-amber-500/30 bg-amber-500/10')
              }>{m.access_level === 'clients' ? 'clinic-wide' : 'own tasks only'}</span>
              {m.default_crew && <span className="text-[10px] font-mono text-emerald-300">default crew</span>}
              {canEdit && (
                <button onClick={() => remove.mutate(m.id)} disabled={remove.isPending} title="Remove from this client"
                  className="text-muted hover:text-red-400"><X size={14} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-muted mt-3">A person's level (clinic-wide or own tasks only) is theirs across all their clinics; change it under Admin, People, Clinics.</p>
    </Card>
    </div>
  );
}


/** What the portal enforces for this client: how many adjustment rounds are
 *  included per creative before the client has to ask KAF PRO for more. */
function TermsCard({ client }: { client: Client }) {
  const qc = useQueryClient();
  const canEdit = useCan('clients.write');
  const [limit, setLimit] = useState<number>(client.client_adjustment_limit ?? 2);
  const save = useMutation({
    mutationFn: () => setClientSettings(client.id, limit),
    onSuccess: () => { toast.success('Portal terms saved'); qc.invalidateQueries({ queryKey: qk.client(client.id) }); },
  });
  return (
    <div data-testid="terms-card">
    <Card className="mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-medium text-muted-strong">Portal terms</h2>
          <p className="text-[11px] text-muted mt-0.5">Adjustment rounds included per creative. Beyond this the portal refuses and the client is told to contact KAF PRO. Extra rounds can be granted per creative with a reason.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min={0} max={20} value={limit} disabled={!canEdit}
            onChange={(e) => setLimit(Math.max(0, Math.min(20, Number(e.target.value) || 0)))}
            aria-label="Included adjustment rounds"
            className="w-20 h-9 rounded-lg border border-slate-800 bg-bg px-3 text-sm text-muted-strong focus:outline-none focus:border-accent-400 disabled:opacity-60" />
          <span className="text-xs text-muted">rounds</span>
          {canEdit && (
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || limit === (client.client_adjustment_limit ?? 2)}>Save</Button>
          )}
        </div>
      </div>
    </Card>
    </div>
  );
}


/** The clinic's portal logins. Any number; approvals are routed to the ones you pick. */
function ContactsCard({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const isManager = useCan('users.invite');
  const canPlanContacts = useCan('creatives.write');
  const [waContact, setWaContact] = useState<{ id: string; display_name: string } | null>(null);
  const contacts = useQuery({ queryKey: qk.clientContacts(clientId), queryFn: () => fetchClientContacts(clientId) });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => inviteClientContact(clientId, email.trim().toLowerCase(), name.trim()),
    onSuccess: (r) => {
      toast.success(`${r.user.display_name} added. Send them the link.`);
      setLink(r.invite_url); setName(''); setEmail(''); setAdding(false);
      qc.invalidateQueries({ queryKey: qk.clientContacts(clientId) });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not add the contact'),
  });
  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-medium text-muted-strong">Client contacts</h2>
          <span className="text-xs text-muted">{contacts.data ? `${contacts.data.length} portal login${contacts.data.length === 1 ? '' : 's'}` : ''}</span>
        </div>
        {isManager && !adding && (
          <Button size="sm" variant="secondary" onClick={() => { setAdding(true); setLink(null); }}>Add contact</Button>
        )}
      </div>
      {(contacts.data ?? []).length === 0 && !adding && (
        <p className="text-xs text-muted">No portal logins yet. Approvals cannot be sent to the clinic until a contact exists.</p>
      )}
      <div className="divide-y divide-slate-800/60">
        {(contacts.data ?? []).map((u) => (
          <div key={u.id} className="py-2.5 text-sm" data-testid="contact-row">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="text-muted-strong">{u.display_name}</span>
                <span className="text-muted text-[11px] ml-2">{u.email}</span>
              </div>
              <span className="text-[11px] text-muted">{seenLabel(u.last_seen_at, u.invited_at)}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
              <span className="inline-flex items-center gap-1.5">WhatsApp <Pill s={{ connected: !!u.whatsapp_linked, on: true, waiting: false }} />
                {u.whatsapp_hint && <span>{u.whatsapp_hint}</span>}
                {canPlanContacts && !u.whatsapp_linked && <button className="text-accent-400 hover:underline" onClick={() => setWaContact(u)} data-testid="contact-wa-connect">connect</button>}</span>
              <span className="inline-flex items-center gap-1.5">Email <Pill s={{ connected: !!u.email_confirmed, on: true, waiting: !u.email_confirmed }} />
                {canPlanContacts && !u.email_confirmed && <button className="text-accent-400 hover:underline"
                  onClick={() => userEmailConfirm(u.id).then((r) => (r.ok ? toast.success(r.message) : toast.error(r.message))).catch((e) => toast.error(errText(e, 'Could not send')))}>send confirm</button>}</span>
            </div>
          </div>
        ))}
        <WhatsAppLinkDialog open={!!waContact} who={waContact?.display_name ?? ''}
          onClose={() => { setWaContact(null); qc.invalidateQueries({ queryKey: qk.clientContacts(clientId) }); }}
          getLink={() => userWhatsAppLink(waContact!.id)}
          isConnected={async () => !!(await fetchClientContacts(clientId)).find((x) => x.id === waContact?.id)?.whatsapp_linked} />
      </div>
      {adding && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Ahmed Asmael" />
          <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@clinic.ae" />
          <Button size="sm" onClick={() => invite.mutate()} loading={invite.isPending} disabled={!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())}>Create login</Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
      {link && (
        <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-600/10 px-3 py-2 text-xs">
          <div className="text-emerald-300 font-medium mb-1">Magic link (valid 30 days). Send it over WhatsApp or email:</div>
          <div className="flex items-center gap-2">
            <code className="truncate text-muted-strong flex-1">{link}</code>
            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(link).then(() => toast.success('Link copied')); }}>Copy</Button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted mt-3">When the team sends a creative for approval it picks which contacts must sign it off; the others can view and comment.</p>
    </Card>
  );
}

function TalentsCard({ client }: { client: Client }) {
  const qc = useQueryClient();
  const canPlan = useCan('creatives.write');
  const [code, setCode] = useState<TalentCode | null>(null);
  const canInvite = useCan('users.invite');
  const [inviteFor, setInviteFor] = useState<{ id: string; name: string; portal_email?: string | null } | null>(null);
  const [portalLink, setPortalLink] = useState<{ who: string; url: string; emailed: boolean } | null>(null);
  const invite = useMutation({
    mutationFn: ({ tid, email }: { tid: string; email: string }) => invitePortalDoctor(client.id, tid, email),
    onSuccess: (r) => { setPortalLink({ who: r.user.display_name, url: r.invite_url, emailed: r.emailed }); setInviteFor(null); if (r.emailed) toast.success(`Invitation emailed to ${r.user.display_name}`);
      else toast.warning('Login created. Email is not connected yet, so send them the link yourself.'); qc.invalidateQueries({ queryKey: ['clients'] }); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not invite'),
  });
  const label = client.taxonomy.talent.label || 'Talent';
  const getCode = useMutation({
    mutationFn: (tid: string) => talentTelegramCode(client.id, tid),
    onSuccess: (r) => setCode(r),
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not issue a code'),
  });
  const unlink = useMutation({
    mutationFn: (tid: string) => unlinkTalentTelegram(client.id, tid),
    onSuccess: () => { toast.success('Unlinked'); setCode(null); qc.invalidateQueries({ queryKey: ['clients'] }); },
  });
  const [contactFor, setContactFor] = useState<Client['talents'][number] | null>(null);
  const [waFor, setWaFor] = useState<Client['talents'][number] | null>(null);
  const refreshClient = () => { qc.invalidateQueries({ queryKey: ['clients'] }); qc.invalidateQueries({ queryKey: qk.client(client.id) }); };
  const confirmEmail = useMutation({
    mutationFn: (tid: string) => talentEmailConfirm(client.id, tid),
    onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
    onError: (e) => toast.error(errText(e, 'Could not send')),
  });
  const waUnlink = useMutation({ mutationFn: (tid: string) => talentWhatsAppUnlink(client.id, tid), onSuccess: () => { toast.success('WhatsApp disconnected'); refreshClient(); } });
  if (!client.taxonomy.talent.enabled) return null;
  const linked = client.talents.filter((t) => t.telegram_linked || t.whatsapp_linked).length;
  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-medium text-muted-strong">{label}s: how they get scripts</h2>
          <span className="text-xs text-muted" data-testid="talents-reach">{linked} of {client.talents.length} reachable</span>
        </div>
      </div>
      <p className="text-xs text-muted mb-3">Save each {label.toLowerCase()}'s phone and email, then connect them. They get each script on WhatsApp and answer with 1 or 2 or on their approval page; Telegram and the portal work too. A number or an email is used only after they confirm it themselves.</p>
      {client.talents.length === 0 && <p className="text-xs text-muted">No {label.toLowerCase()}s yet. Add them in the taxonomy editor.</p>}
      <div className="divide-y divide-slate-800/60">
        {client.talents.map((t) => (
          <div key={t.id} className="py-3 text-sm" data-testid="talent-row">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-muted-strong">{t.name}</div>
                <div className="text-[11px] text-muted">{[t.phone, t.email].filter(Boolean).join(' · ') || 'no phone or email saved yet'}</div>
              </div>
              {canPlan && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setContactFor(t)} data-testid="talent-contact">Contact</Button>
                  {!t.whatsapp_linked
                    ? <Button size="sm" onClick={() => setWaFor(t)} data-testid="talent-wa-connect">Connect WhatsApp</Button>
                    : <Button size="sm" variant="ghost" onClick={() => waUnlink.mutate(t.id)} disabled={waUnlink.isPending}>Disconnect WhatsApp</Button>}
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted">
              <span className="inline-flex items-center gap-1.5" data-testid="talent-wa">WhatsApp <Pill s={{ connected: !!t.whatsapp_linked, on: true, waiting: !!t.phone && !t.whatsapp_linked }} />{t.whatsapp_hint && <span>{t.whatsapp_hint}</span>}</span>
              <span className="inline-flex items-center gap-1.5">Email <Pill s={{ connected: !!t.email_confirmed, on: true, waiting: !!t.email && !t.email_confirmed }} />
                {canPlan && t.email && !t.email_confirmed && <button className="text-accent-400 hover:underline" onClick={() => confirmEmail.mutate(t.id)}>resend confirm</button>}</span>
              <span className="inline-flex items-center gap-1.5">Telegram <Pill s={{ connected: !!t.telegram_linked, on: true, waiting: false }} />
                {canPlan && (!t.telegram_linked
                  ? <button className="text-accent-400 hover:underline" onClick={() => getCode.mutate(t.id)} disabled={getCode.isPending}>connect</button>
                  : <button className="text-accent-400 hover:underline" onClick={() => unlink.mutate(t.id)} disabled={unlink.isPending}>unlink</button>)}</span>
              <span className="inline-flex items-center gap-1.5" data-testid="talent-portal">Portal <Pill s={{ connected: !!t.portal_email, on: true, waiting: false }} />
                {canInvite && <button className="text-accent-400 hover:underline" onClick={() => setInviteFor(t)} data-testid="talent-invite">{t.portal_email ? 'new link' : 'invite'}</button>}</span>
            </div>
          </div>
        ))}
      </div>
      {code && (
        <div className="mt-3 rounded-lg border border-slate-800 bg-bg px-3 py-2 text-xs space-y-2">
          <div className="text-muted-strong">Send {code.talent_name} these two lines (WhatsApp is fine). The code works once.</div>
          <div className="font-mono text-muted-strong break-all">1. Open {code.bot_url}  2. Send: {code.command}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" icon={<Copy size={12} />}
              onClick={() => { navigator.clipboard?.writeText(`Please open ${code.bot_url} and send this message to the bot: ${code.command}`); toast.success('Copied'); }}>
              Copy the instructions
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setCode(null)}>Done</Button>
          </div>
        </div>
      )}
      {portalLink && (
        <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-600/10 px-3 py-2 text-xs space-y-1" data-testid="talent-portal-link">
          <div className="text-emerald-300 font-medium">{portalLink.emailed ? `Emailed to ${portalLink.who}. The same link, if you want to send it yourself (valid 30 days):` : `Email is not connected, so nothing was sent. Send ${portalLink.who} this link yourself (valid 30 days):`}</div>
          <div className="flex items-center gap-2">
            <code className="truncate text-muted-strong flex-1">{portalLink.url}</code>
            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(portalLink.url).then(() => toast.success('Link copied')); }}>Copy</Button>
            <Button size="sm" variant="secondary" onClick={() => setPortalLink(null)}>Done</Button>
          </div>
        </div>
      )}
      <TalentContactDialog clientId={client.id} talent={contactFor} onClose={() => setContactFor(null)} onSaved={refreshClient} />
      <WhatsAppLinkDialog open={!!waFor} who={waFor?.name ?? ''} onClose={() => { setWaFor(null); refreshClient(); }}
        getLink={() => talentWhatsAppLink(client.id, waFor!.id)}
        isConnected={async () => !!(await fetchClient(client.id)).talents.find((x) => x.id === waFor?.id)?.whatsapp_linked} />
      <PromptDialog open={!!inviteFor} title={inviteFor ? `Portal login for ${inviteFor.name}` : ''} message="Their email. They get a sign-in link there, and see their scripts, their pieces in production and their videos." placeholder="doctor@clinic.com" defaultValue={inviteFor?.portal_email ?? ''} confirmLabel="Send the invitation"
        onCancel={() => setInviteFor(null)} onConfirm={async (v) => { if (!inviteFor) return; await invite.mutateAsync({ tid: inviteFor.id, email: v.trim().toLowerCase() }); }} />
    </Card>
  );
}

/** The doctor's phone and email, typed by the planner. The email gets a confirm link at
 *  once; the phone is used only after the doctor sends their code from it. */
function TalentContactDialog({ clientId, talent, onClose, onSaved }: {
  clientId: string; talent: Client['talents'][number] | null; onClose: () => void; onSaved: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [openFor, setOpenFor] = useState<string | null>(null);
  if (talent && openFor !== talent.id) { setOpenFor(talent.id); setPhone(talent.phone ?? ''); setEmail(talent.email ?? ''); }
  if (!talent && openFor) setOpenFor(null);
  const save = useMutation({
    mutationFn: () => saveTalentContact(clientId, talent!.id, { phone: phone.trim(), ...(email.trim() ? { email: email.trim() } : {}) }),
    onSuccess: (r) => { toast.success(r.confirm_sent ? `Saved. A confirm link went to ${r.email}.` : 'Saved'); onSaved(); onClose(); },
    onError: (e) => toast.error(errText(e, 'Could not save')),
  });
  if (!talent) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="talent-contact-dialog">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-md w-full shadow-card">
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div>
            <h3 className="font-medium text-muted-strong">{talent.name}: contact</h3>
            <p className="text-xs text-muted">Used for scripts and call links, only after they confirm.</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-muted-strong" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Input label="WhatsApp number" inputMode="tel" dir="ltr" placeholder="+971 50 123 4567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="talent-phone" />
          <Input label="Email" type="email" dir="ltr" placeholder="doctor@clinic.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="talent-email" />
          <p className="text-[11px] text-muted">A new email gets a one-click confirm link now. For WhatsApp, save the number, then use Connect WhatsApp so they send their code.</p>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} data-testid="talent-contact-save">Save</Button>
        </div>
      </div>
    </div>
  );
}
